<!-- Личные уведомления и общий счётчик непрочитанных текущей компании. -->
<template>
  <div class="notification-bell-container" @pointerenter="openOnHover" @pointerleave="closeOnLeave">
    <BaseTooltip text="Уведомления" position="bottom" :disabled="popupOpen">
      <button
        ref="trigger"
        type="button"
        class="notification-bell"
        :aria-expanded="popupOpen"
        :aria-controls="popupOpen ? 'notification-popup' : undefined"
        @click="openNotifications"
        @keydown.down.prevent="openFromKeyboard"
        :aria-label="label"
      >
        <AppIcon
          :key="store.bellPulse"
          name="bell"
          :size="23"
          class="notification-bell__icon"
          :class="{ 'notification-bell__icon--ringing': store.bellPulse > 0 }"
          aria-hidden="true"
        />
        <span v-if="store.badge" class="notification-bell__badge" aria-hidden="true">
          {{ store.badge }}
        </span>
        <span v-if="store.otherBadge" class="notification-bell__other-badge" aria-hidden="true">
          <span>{{ store.otherBadge }}</span>
        </span>
      </button>
    </BaseTooltip>
    <Popup v-if="popupOpen" ref="popup" @close="closePopup(true)" />
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import Popup from "@/components/Notification/Popup.vue";
import { useNotificationCounterStore } from "@/stores/notificationCounterStore";
import { useUserDataStore } from "@/stores/userDataStore";

export default {
  name: "NotificationBell",
  components: { AppIcon, Popup },
  data() {
    return { popupOpen: false, closeTimer: null };
  },
  computed: {
    label() {
      const current = this.store.unreadCount || 0;
      const other = this.store.otherUnreadCount || 0;
      return `Уведомления: ${current} непрочитанных в текущей компании, ${other} в других компаниях`;
    },
    store() {
      return useNotificationCounterStore();
    },
    context() {
      const user = useUserDataStore().userData;
      return `${user?.id}:${user?.current_company?.id}`;
    },
  },
  watch: {
    context: {
      immediate: true,
      handler() {
        this.closePopup();
        this.store.startLive();
      },
    },
    "$route.fullPath"() {
      this.closePopup();
    },
  },
  mounted() {
    document.addEventListener("pointerdown", this.closeOutside);
    document.addEventListener("focusin", this.closeOutside);
    document.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("resize", this.onResize);
    document.addEventListener("pointerdown", this.prepareSound);
    document.addEventListener("keydown", this.prepareSound);
  },
  beforeUnmount() {
    clearTimeout(this.closeTimer);
    document.removeEventListener("pointerdown", this.closeOutside);
    document.removeEventListener("focusin", this.closeOutside);
    document.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("pointerdown", this.prepareSound);
    document.removeEventListener("keydown", this.prepareSound);
    this.store.reset();
  },
  methods: {
    prepareSound() {
      this.store.prepareSound();
    },
    openNotifications() {
      this.closePopup();
      this.$router.push({ name: "Notifications" });
    },
    openOnHover(event) {
      if (event.pointerType === "touch" || !window.matchMedia("(min-width: 768px) and (hover: hover)").matches) return;
      clearTimeout(this.closeTimer);
      this.popupOpen = true;
    },
    closeOnLeave() {
      clearTimeout(this.closeTimer);
      // Даём курсору пересечь отступ или зайти в popup по диагонали.
      this.closeTimer = setTimeout(() => {
        if (!this.$refs.popup?.$el.contains(document.activeElement)) this.closePopup();
      }, 400);
    },
    openFromKeyboard() {
      if (window.matchMedia("(max-width: 767px)").matches) return;
      clearTimeout(this.closeTimer);
      this.popupOpen = true;
      this.$nextTick(() => this.$refs.popup?.$el.focus());
    },
    closePopup(restoreFocus = false) {
      clearTimeout(this.closeTimer);
      const wasOpen = this.popupOpen;
      this.popupOpen = false;
      if (wasOpen && restoreFocus) this.$refs.trigger?.focus();
    },
    closeOutside(event) {
      if (!this.$el.contains(event.target)) this.closePopup();
    },
    onKeyDown(event) {
      if (event.key === "Escape" && this.popupOpen) {
        event.preventDefault();
        this.closePopup(true);
      }
    },
    onResize() {
      if (window.matchMedia("(max-width: 767px)").matches) this.closePopup();
    },
  },
};
</script>

<style scoped>
.notification-bell-container {
  position: relative;
}

.notification-bell {
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 8px;
  color: color-mix(in srgb, var(--color-dark) 65%, var(--color-light));
}

.notification-bell__badge {
  position: absolute;
  top: 1px;
  right: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: var(--color-green);
  color: var(--color-light);
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}

.notification-bell__icon {
  transform-origin: 50% 20%;
}

@media (prefers-reduced-motion: no-preference) {
  .notification-bell__icon--ringing {
    animation: notification-bell-ring 600ms ease-in-out;
  }
}

@keyframes notification-bell-ring {
  0%, 100% { transform: rotate(0); }
  20% { transform: rotate(12deg); }
  40% { transform: rotate(-10deg); }
  60% { transform: rotate(6deg); }
  80% { transform: rotate(-3deg); }
}

.notification-bell:hover {
  color: var(--color-dark);
}

.notification-bell__other-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 2px 4px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  color: color-mix(in srgb, var(--color-dark) 75%, var(--color-light));
  background: var(--color-grey);
}

@media (max-width: 767px) {
  .notification-bell__other-badge {
    width: 6px;
    height: 6px;
    padding: 0;
    bottom: 6px;
    right: 5px;
    background: color-mix(in srgb, var(--color-dark) 55%, var(--color-light));
  }
  .notification-bell__other-badge span {
    display: none;
  }
}

.notification-bell:focus-visible {
  outline: 2px solid var(--color-green);
  outline-offset: 2px;
}
</style>
